<script setup lang="ts">
  import { ref, watch, inject, computed, onMounted, type Ref } from 'vue'
  import { Vue3Lottie } from 'vue3-lottie'

  const props = withDefaults(
    defineProps<{
      animationData?: object | string
      width?: string | number
      height?: string | number
      loop?: boolean
    }>(),
    {
      animationData: undefined,
      width: '100%',
      height: 'auto',
      loop: false,
    },
  )

  const player = ref<typeof Vue3Lottie | null>(null)

  // Inject the active slide from parent OnboardingSliderPage
  const activeSlide = inject<Ref<number>>('activeSlide', ref(-1))

  // Inject the slide index from parent OnboardingSlide
  const slideIndex = inject<number>('slideIndex', -1)

  // Auto-determine playing state based on active slide
  const isPlaying = computed(() => {
    return activeSlide.value === slideIndex
  })

  // Check if animationData is valid
  const hasValidAnimationData = computed(() => {
    return props.animationData !== undefined && props.animationData !== null
  })

  // Watch the playing state to control animation
  watch(
    isPlaying,
    (playing) => {
      console.log(
        'Lottie control - slideIndex:',
        slideIndex,
        'playing:',
        playing,
      )
      if (player.value) {
        try {
          if (playing) {
            player.value.play()
          } else {
            player.value.pause()

            // Reset animation to start after a delay, but only if still not playing
            setTimeout(() => {
              if (player.value && !isPlaying.value) {
                player.value.stop() // stop() resets to beginning
              }
            }, 500)
          }
        } catch (error) {
          console.warn('Lottie animation control error:', error)
        }
      }
    },
    { immediate: false }, // Don't run immediately, wait for component to be ready
  )

  // Handle when animation is loaded and ready
  const onAnimationLoaded = () => {
    console.log('Animation loaded, current playing state:', isPlaying.value)
    setTimeout(() => {
      if (player.value) {
        try {
          if (isPlaying.value) {
            player.value.play()
          } else {
            player.value.stop()
          }
        } catch (error) {
          console.warn('Lottie animation loaded control error:', error)
        }
      }
    }, 50)
  }

  // Ensure the animation state is correct when mounted
  onMounted(() => {
    if (player.value) {
      try {
        if (isPlaying.value) {
          player.value.play()
        } else {
          player.value.stop()
        }
      } catch (error) {
        console.warn('Lottie animation mount error:', error)
      }
    }
  })

  // Expose methods for external control
  const resetToStart = () => {
    if (player.value) {
      try {
        player.value.stop() // stop() resets animation to beginning
      } catch (error) {
        console.warn('Lottie reset error:', error)
      }
    }
  }

  const play = () => {
    if (player.value) {
      try {
        player.value.play()
      } catch (error) {
        console.warn('Lottie play error:', error)
      }
    }
  }

  const pause = () => {
    if (player.value) {
      try {
        player.value.pause()
      } catch (error) {
        console.warn('Lottie pause error:', error)
      }
    }
  }

  defineExpose({
    resetToStart,
    play,
    pause,
  })
</script>
<template>
  <lottie-player
    v-if="hasValidAnimationData"
    ref="player"
    :animation-data="props.animationData"
    :width="props.width"
    :height="props.height"
    :loop="props.loop"
    :autoplay="false"
    @on-animation-loaded="onAnimationLoaded"
  />
  <div
    v-else
    class="lottie-placeholder"
    :style="{ width: props.width, height: props.height }"
  >
    <!-- Placeholder when no valid animationData is provided -->
  </div>
</template>
<style scoped>
  .lottie-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.05);
    border-radius: 8px;
  }
</style>
